import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiFunctionsEnum } from 'constants/api.constants';
import { customerAccountDetailsTypes } from 'constants/customer-account.constants';
import ApiService from './api.service';
import { setError } from 'state/reducers/webhook-monitoring.reducer';
import { buildPaginationQueryParams, setErrorMessage } from 'utils/index';

type WebhookMonitoringDetailRequest = {
	detailType: string;
	companyId: number;
	detailId: number;
};

type WebhookMonitoringBusinessDataRequest = {
	apiFunction: apiFunctionsEnum;
	params: Record<string, number>;
	responseKey: string;
};

const buildPaymentBusinessDataRequest = (
	detailRequest: WebhookMonitoringDetailRequest
): WebhookMonitoringBusinessDataRequest => ({
	apiFunction: apiFunctionsEnum.GET_COMPANY_PAYMENT_BY_ID,
	params: { id: detailRequest.companyId, paymentId: detailRequest.detailId },
	responseKey: 'payment',
});

const buildConversionBusinessDataRequest = (
	detailRequest: WebhookMonitoringDetailRequest
): WebhookMonitoringBusinessDataRequest => ({
	apiFunction: apiFunctionsEnum.GET_COMPANY_CONVERSION_BY_ID,
	params: { id: detailRequest.companyId, conversionId: detailRequest.detailId },
	responseKey: 'conversion',
});

const buildBalanceBusinessDataRequest = (
	detailRequest: WebhookMonitoringDetailRequest
): WebhookMonitoringBusinessDataRequest => ({
	apiFunction: apiFunctionsEnum.GET_COMPANY_BALANCE_BY_ID,
	params: { id: detailRequest.companyId, balanceId: detailRequest.detailId },
	responseKey: 'balance',
});

const buildBalanceRefundBusinessDataRequest = (
	detailRequest: WebhookMonitoringDetailRequest
): WebhookMonitoringBusinessDataRequest => ({
	apiFunction: apiFunctionsEnum.GET_COMPANY_BALANCE_REFUND_BY_ID,
	params: { id: detailRequest.companyId, refundId: detailRequest.detailId },
	responseKey: 'balanceRefund',
});

const webhookMonitoringBusinessDataRequestByDetailType: Record<
	string,
	(detailRequest: WebhookMonitoringDetailRequest) => WebhookMonitoringBusinessDataRequest
> = {
	[customerAccountDetailsTypes.PAYMENT]: buildPaymentBusinessDataRequest,
	[customerAccountDetailsTypes.CONVERSION]: buildConversionBusinessDataRequest,
	[customerAccountDetailsTypes.BALANCE]: buildBalanceBusinessDataRequest,
	[customerAccountDetailsTypes.BALANCE_WITHDRAWAL]: buildBalanceBusinessDataRequest,
	[customerAccountDetailsTypes.BALANCE_REFUND]: buildBalanceRefundBusinessDataRequest,
};

export const getWebhookMonitoringAnalytics = createAsyncThunk(
	'webhookMonitoring/getAnalytics',
	async (_, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_WEBHOOK_MONITORING_ANALYTICS,
				{}
			);
			return response.data;
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return rejectWithValue(error);
		}
	}
);

export const getWebhookMonitoringOperations = createAsyncThunk(
	'webhookMonitoring/getOperations',
	async (
		params: {
			status: string;
			offset: number;
			limit: number;
			sortBy: string;
			sortOrder: 'asc' | 'desc';
			filters?: Record<string, any>;
		},
		{ rejectWithValue }
	) => {
		try {
			const queryParams = buildPaginationQueryParams(params);
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_WEBHOOK_MONITORING_OPERATIONS,
				{
					status: params.status,
					...queryParams,
				}
			);
			return response.data;
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return rejectWithValue(error);
		}
	}
);

export const replayWebhookMonitoringOperation = createAsyncThunk(
	'webhookMonitoring/replayOperation',
	async (operationId: string, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.REPLAY_WEBHOOK_MONITORING_OPERATION,
				{
					operationId,
				}
			);
			return response.data;
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return rejectWithValue(error);
		}
	}
);

export const getWebhookMonitoringOperationDetails = createAsyncThunk(
	'webhookMonitoring/getOperationDetails',
	async (row: any, { rejectWithValue }) => {
		try {
			const operationResponse = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_WEBHOOK_MONITORING_OPERATION_DETAILS,
				{
					operationId: row.operationId,
				}
			);

			const detailRequest = operationResponse.data.detailRequest;
			if (!detailRequest) {
				throw new Error('webhook_monitoring_detail_request_missing');
			}

			let businessData = operationResponse.data.businessData;
			if (businessData == null) {
				const buildRequest = webhookMonitoringBusinessDataRequestByDetailType[detailRequest.detailType];
				if (!buildRequest) {
					throw new Error('webhook_monitoring_business_data_missing');
				}
				const businessRequest = buildRequest(detailRequest);
				const businessResponse = await ApiService.getInstance().execute(
					businessRequest.apiFunction,
					businessRequest.params
				);
				businessData = businessResponse.data[businessRequest.responseKey];
				if (!businessData) {
					throw new Error('webhook_monitoring_business_data_missing');
				}
			}

			return {
				row,
				detailType: detailRequest.detailType,
				operation: operationResponse.data.operation,
				attempts: operationResponse.data.attempts,
				attemptsTotal: operationResponse.data.attemptsTotal,
				businessData,
			};
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return rejectWithValue(error);
		}
	}
);

export const getWebhookMonitoringHealthcheck = createAsyncThunk(
	'webhookMonitoring/getHealthcheck',
	async (_, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_WEBHOOK_MONITORING_HEALTHCHECK,
				{}
			);
			return response.data;
		} catch (error: any) {
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			return rejectWithValue(error);
		}
	}
);
